import React, { useState } from 'react';
import { User, AssessmentRecord } from '../types';
import { db } from '../services/mockDb';
import ResultsView from './ResultsView';
import { toLegacyGradingReport } from '../services/reportAdapters';

interface StudentDashboardProps {
  user: User;
}

const StudentDashboard: React.FC<StudentDashboardProps> = ({ user }) => {
  const [selectedAssessment, setSelectedAssessment] = useState<AssessmentRecord | null>(null);
  
  // Fetch assessments for this student
  const myAssessments = db.getAssessmentsByStudent(user.name);

  if (selectedAssessment) {
    return (
      <div className="animate-fade-in">
        <button 
          onClick={() => setSelectedAssessment(null)}
          className="mb-6 flex items-center gap-2 text-gray-600 hover:text-gray-900 transition-colors font-medium"
        >
          <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 19l-7-7m0 0l7-7m-7 7h18" />
          </svg>
          Back to My Grades
        </button>
        <ResultsView 
          reports={[toLegacyGradingReport(selectedAssessment.report)]}
          onReset={() => setSelectedAssessment(null)}
          fileLinks={selectedAssessment.fileLinks} 
        />
      </div>
    );
  }

  return (
    <div className="max-w-5xl mx-auto animate-fade-in">
      <header className="mb-10">
        <h1 className="text-3xl font-bold text-gray-900">Welcome back, {user.name}! 👋</h1>
        <p className="text-gray-600 mt-2">Here is an overview of your recent graded assignments.</p>
      </header>

      {myAssessments.length === 0 ? (
        <div className="text-center py-20 bg-white rounded-2xl border border-dashed border-gray-300">
          <div className="text-4xl mb-4">📝</div>
          <h3 className="text-lg font-medium text-gray-900">No grades available yet</h3>
          <p className="text-gray-500">Check back later once your teacher has graded your work.</p>
        </div>
      ) : (
        <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-3">
          {myAssessments.map((assessment) => (
            <div 
              key={assessment.id}
              onClick={() => setSelectedAssessment(assessment)}
              className="bg-white rounded-xl shadow-sm border border-gray-200 p-6 cursor-pointer hover:shadow-md hover:border-primary-300 transition-all group"
            >
              <div className="flex justify-between items-start mb-4">
                <span className="inline-block px-2 py-1 text-xs font-semibold bg-blue-50 text-blue-700 rounded-md">
                  {assessment.subject}
                </span>
                <span className="text-xs text-gray-400">
                  {new Date(assessment.dateGraded).toLocaleDateString()}
                </span>
              </div>
              
              <div className="flex items-end gap-2 mb-2">
                <span className={`text-4xl font-bold ${
                  assessment.report.percentage >= 90 ? 'text-green-600' :
                  assessment.report.percentage >= 70 ? 'text-blue-600' :
                  'text-orange-600'
                }`}>
                  {assessment.report.percentage}%
                </span>
                <span className="text-sm text-gray-400 mb-1.5">/ 100</span>
              </div>
              
              <h3 className="font-semibold text-gray-900 mb-2">Grade {assessment.gradeLevel} Assessment</h3>
              <p className="text-sm text-gray-500 line-clamp-2">
                {toLegacyGradingReport(assessment.report).overallFeedback}
              </p>
              
              <div className="mt-4 pt-4 border-t border-gray-100 flex items-center justify-between">
                <div className="text-primary-600 text-sm font-medium group-hover:translate-x-1 transition-transform">
                  View Feedback →
                </div>
                {assessment.fileLinks?.studentSheet && (
                   <div className="text-xs text-gray-400 flex items-center gap-1" title="Stored in Drive">
                     <svg className="w-3 h-3" fill="currentColor" viewBox="0 0 24 24"><path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm-1 17.93c-3.95-.49-7-3.85-7-7.93 0-.62.08-1.21.21-1.79L9 15v1c0 1.1.9 2 2 2v1.93zm6.9-2.54c-.26-.81-1-1.39-1.9-1.39h-1v-3c0-.55-.45-1-1-1H8v-2h2c.55 0 1-.45 1-1V7h2c1.1 0 2-.9 2-2v-.41c2.93 1.19 5 4.06 5 7.41 0 2.08-.8 3.97-2.1 5.39z"/></svg>
                   </div>
                )}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default StudentDashboard;
